import { v4 as uuidv4 } from "uuid";

/** Header carrying the per-tab browser session id. */
export const SESSION_ID_HEADER = "X-Client-Session-Id";

/**
 * Header marking a request's session as a real per-tab browser visit (traffic
 * telemetry T15). Emitted ONLY by the browser client, and ONLY when its session id
 * is a persisted per-tab id (see `getClientSession`). Its presence is what makes a
 * session id eligible to count as a distinct visitor: the SSR client (a fresh uuid
 * per render) and the ephemeral fallback (privacy mode) omit it, so our own renders
 * never masquerade as visitors. nAPI's traffic sensor reads it (as the lowercase
 * `x-client-session-kind`); the value literal is mirrored there.
 */
export const SESSION_KIND_HEADER = "X-Client-Session-Kind";

/** The visit-eligible marker value — a persisted per-tab browser session. */
export const SESSION_KIND_BROWSER = "browser";

/** Header carrying the per-BROWSER visitor id — what a traffic "visit" is counted on. */
export const VISITOR_ID_HEADER = "X-Client-Visitor-Id";

/**
 * Header carrying the page that is HOSTING the app (never the query string), so nAPI's
 * traffic sensor can record where the app is actually loaded (the "Hosted at" report fact,
 * T16). Emitted ONLY by the browser client, at two DELIBERATELY different grains: when the
 * app runs top-level this is `host + its mount point + /…` (e.g. `web26.hudsonltd.net/a/r/be2.0/...`),
 * collapsing in-app routing to one row per host+mount; when the app is embedded in an iframe it
 * is the SURROUNDING page in FULL — its full URL when the parent is same-origin/readable, else
 * just the parent's host (browsers hide a cross-origin parent's path), and NEVER the app's own
 * URL. Its purpose is to answer "which page is hosting the booking engine?" — most usefully, to
 * spot an unexpected host serving or framing it. SSR renders (no `window`) send nothing. nAPI
 * reads it as the lowercase `x-client-host-page`. See `getClientHostPage`. */
export const HOST_PAGE_HEADER = "X-Client-Host-Page";

/**
 * Reserved `X-Client-Host-Page` value for a request the browser knows is EMBEDDED but whose
 * framing page it cannot see (a cross-origin parent with no `ancestorOrigins` and no readable
 * `Referer`). Sent so an unidentifiable embed shows as its own "Embedded, unknown URL" bucket
 * in the report instead of the request silently vanishing. Chosen to satisfy nAPI's host
 * charset (`[a-z0-9.-]`) so it stores as an ordinary value; the hub maps it to a friendly
 * label. NEVER used for a non-embedded load (that always reports the app's own mount). */
export const HOST_PAGE_EMBEDDED_UNKNOWN = "embedded-unknown";

const SESSION_STORAGE_KEY = "hudson.clientSessionId";

/** localStorage key for the per-browser visitor id (distinct from the per-tab session). */
const VISITOR_STORAGE_KEY = "hudson.clientVisitorId";

/**
 * Accepted shape for a stored visitor id before it is forwarded as a header: the same
 * bounded token nAPI validates (`[A-Za-z0-9_-]`, 1–64; a uuidv4 is 36 chars). A stored
 * value that violates it (corruption, a manual localStorage edit) is re-minted rather
 * than forwarded — a newline or other off-token char in a header value throws in `fetch`
 * before the request leaves, and localStorage poison would outlive the tab.
 */
const VISITOR_ID_SHAPE = /^[A-Za-z0-9_-]{1,64}$/;

/** A resolved client session: its id, and whether that id is a persisted per-tab one. */
export interface ClientSession {
  /** The session id sent as `X-Client-Session-Id`. */
  id: string;
  /**
   * True when `id` was read from / written to sessionStorage — a real, stable
   * per-tab browser session, eligible to count as a distinct VISIT (T15). False for
   * the ephemeral fallback (SSR, privacy mode, sandboxed iframe), whose id is fresh
   * per call and must NEVER be counted as a visitor.
   */
  persisted: boolean;
}

/**
 * The session for this tab, and whether it is a persisted (visit-shaped) one.
 * Persisted in sessionStorage so it survives client re-creation (config changes) but
 * is unique per tab. Falls back to a fresh, NON-persisted id when sessionStorage is
 * unavailable (SSR, privacy mode, sandboxed iframe) — a fresh id per call that is not
 * a countable visit. The server client mints its own fresh uuid and never marks it,
 * so only real per-tab browser sessions are ever eligible to count as visitors.
 */
export function getClientSession(): ClientSession {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (existing) return { id: existing, persisted: true };
      const generated = uuidv4();
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, generated);
      return { id: generated, persisted: true };
    }
  } catch {
    // sessionStorage access can throw — fall through to an ephemeral id.
  }
  return { id: uuidv4(), persisted: false };
}

/**
 * The per-BROWSER visitor id for this origin ("visits" = distinct browsers/devices —
 * Adam, 2026-08-18): a random id minted once per browser per origin and kept in
 * localStorage, so every tab, reload and return from the same browser carries the same
 * id and nAPI's visit sketch counts that browser once. Deliberately SEPARATE from the
 * per-tab session id, which also rides to Bridge as the caller's app session for log
 * correlation and would become a long-lived per-device identifier in Bridge's logs if
 * it were made persistent — this id changes only what a "visit" counts. Not tied to any
 * login or user; count-only on the server (HyperLogLog), never stored readably.
 *
 * Null when localStorage is unavailable (privacy mode that blocks it, sandboxed iframe,
 * SSR). Null means "send no visitor header" — an id that changed per call would count
 * one browser as many visitors, so the caller omits the header and that browser is
 * simply not counted as a visit (nAPI never substitutes the per-tab session id: the
 * figure is per-browser or *unknown*, never a blend of the two definitions).
 */
export function getClientVisitorId(): string | null {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      const existing = window.localStorage.getItem(VISITOR_STORAGE_KEY);
      if (existing && VISITOR_ID_SHAPE.test(existing)) return existing;
      const generated = uuidv4();
      window.localStorage.setItem(VISITOR_STORAGE_KEY, generated);
      return generated;
    }
  } catch {
    // localStorage access can throw — no visitor id for this browser.
  }
  return null;
}

/** Max length of the hosting-page value the browser sends (host + path). Matches nAPI's own
 * read cap (`HOSTPAGE_MAX_LENGTH`), so we never ship bytes nAPI would only truncate away. */
const HOST_PAGE_MAX = 128;

/**
 * The page HOSTING the app right now, for nAPI's "Hosted at" traffic fact (T16). Read fresh
 * per request (the caller builds headers per operation). Sources, in priority order:
 *
 * - Top-level load (NOT embedded): the app is running on its own — we only need to know
 *   WHERE it is mounted, not which in-app page. Collapse to `host + the app's mount point`
 *   (`process.env.basePath`, e.g. `/book` or the legacy `/a/r/be2.0`) + `/…`, dropping every
 *   in-app route. So a booking funnel's ~20 page URLs become ONE row per host+mount, and the
 *   mount itself flags which line / whether the host should be serving the app. The
 *   `parentUrlHint` is IGNORED here — it records a FRAMING page, which a top-level load has none.
 * - Embedded, WITH a `parentUrlHint` — the framing page's OWN URL, told to us outright by the
 *   embed. The CMS/maFrame snippet passes the parent page's URL into the iframe (BE's
 *   `ac[current_parent_url]`, persisted in FrameConfig), and the app hands it in here. This is
 *   the authoritative answer to "which page is hosting the engine" and is browser-independent —
 *   it works where the same-origin peek below is BLOCKED, which is the norm: the engine runs on
 *   its own host (`reservations.customer.com` / a bare `customer.com`) while the framing page is
 *   a different host (`www.customer.com/city`), so the browser hides the parent's path and the
 *   fallbacks below can only ever see the bare host. Used whenever parseable; only its ABSENCE
 *   falls through to those fallbacks.
 * - Embedded, no hint: best-effort detection of the SURROUNDING page. A same-origin
 *   (readable) parent yields its full `hostname + pathname`; a cross-origin parent yields
 *   only its host (via `ancestorOrigins`, else the `Referer`), because browsers hide a
 *   cross-origin parent's path.
 *
 * The query string is always dropped (it can carry reservation ids; only page identity is
 * wanted). Returns null on SSR, or when nothing can be read — the caller then sends no header,
 * exactly like the visitor id. Never throws: a cross-origin / sandboxed access that throws
 * must not break every request from a browser.
 */
export function getClientHostPage(parentUrlHint?: string): string | null {
  try {
    if (typeof window === "undefined") return null;
    const framed = window.self !== window.top;
    if (!framed) {
      // A direct top-level load IS the page — report its own mount, never a hint. (The hint is
      // an embed-time record of a FRAMING page; a top-level load has none, and a value left in
      // per-tab storage from an earlier context must not leak onto it.)
      return collapseOwnMount(window.location.hostname);
    }
    // EMBEDDED. The embed told us the framing page outright — authoritative, and the ONLY source
    // that survives a cross-host parent (the norm: engine on its own host, site on another).
    // Prefer it; only its absence falls back to best-effort detection of the parent below.
    if (parentUrlHint) {
      const fromHint = hostPageFromUrl(parentUrlHint);
      if (fromHint) return fromHint;
    }
    // Prefer the readable (same-origin) top page's full URL, else its host only.
    try {
      const top = window.top;
      if (top) {
        // Only accept a non-null value — an about:blank / opaque parent can yield an empty
        // hostname WITHOUT throwing, so fall through to ancestorOrigins / referrer rather than
        // returning null and losing the host-only signal those still provide.
        const parent = normalizeHostPage(top.location.hostname, top.location.pathname);
        if (parent) return parent;
      }
    } catch {
      // Cross-origin parent — its location is unreadable; fall through to host-only.
    }
    const origins = window.location.ancestorOrigins;
    const outermost = origins && origins.length > 0 ? origins[origins.length - 1] : undefined;
    if (outermost) {
      try {
        return normalizeHostPage(new URL(outermost).hostname, "");
      } catch {
        // Malformed origin — try the referrer next.
      }
    }
    if (typeof document !== "undefined" && document.referrer) {
      try {
        const refHost = new URL(document.referrer).hostname;
        // Only the PARENT's host, NEVER our own. After a same-origin navigation INSIDE the
        // iframe, `document.referrer` is the app's own previous page — and on a browser without
        // `ancestorOrigins` (Firefox) this Referer path is the ONLY cross-origin fallback, so an
        // embedded app that navigates internally would otherwise report its own serving host here
        // (the exact first-party self-report this feature removes). A Referer on our own host is a
        // self-referral: skip it and fall through to the sentinel.
        if (refHost && refHost.toLowerCase() !== window.location.hostname.toLowerCase()) {
          const parent = normalizeHostPage(refHost, "");
          if (parent) return parent;
        }
      } catch {
        // Malformed referrer — nothing to report.
      }
    }
    // Embedded but the parent is undisclosable (cross-origin, no ancestorOrigins, no usable Referer):
    // report the reserved "embedded, unknown URL" bucket so the embed still shows up, rather
    // than vanishing. An embedded app must NEVER report its OWN mount here (this branch never
    // touches `window.location`) — only the surrounding page, or this sentinel.
    return HOST_PAGE_EMBEDDED_UNKNOWN;
  } catch {
    // A genuinely unexpected failure (not the embedded-undisclosable case) — send no header.
    return null;
  }
}

/**
 * The NON-embedded value: `host + the app's mount point + "/…"`, collapsing all in-app
 * routing to one row per host+mount (T16). `process.env.basePath` is each app's Next
 * `basePath`, inlined into the browser bundle at build (e.g. `/book`, the legacy `/a/r/be2.0`;
 * "" in dev or for a mount-less app). Deliberately does NOT read `window.location.pathname`:
 * a direct load only needs "where is it mounted", never which in-app page — that is what keeps
 * the funnel's ~20 URLs from each becoming a row. Used only from the non-embedded branch.
 *
 * The mount is passed through unvalidated here, so it MUST stay within nAPI's host-page path
 * charset (`HOSTPAGE_PATH_REGEX` = `[A-Za-z0-9._~/-]` in apps/napi/src/server/trafficAttribution.ts)
 * — every `PUBLIC_URL` today (`/book`, `/a/r/be2.0`, `/widgets`) does. A future mount with a
 * character outside it (a `%`-encoding, a sub-delim like `@+,;=:`) would fail nAPI's validation and
 * silently drop EVERY top-level "Hosted at" row for that line (absence is a valid bucket, so nothing
 * flags it). Keep the two charsets in step, or add a client-side guard here, if a mount needs one.
 */
function collapseOwnMount(hostname: string): string | null {
  const host = hostname.trim().toLowerCase();
  if (!host) return null;
  let mount = (process.env.basePath || "").trim();
  if (mount && !mount.startsWith("/")) mount = `/${mount}`;
  mount = mount.replace(/\/+$/, "");
  return `${host}${mount}/...`.slice(0, HOST_PAGE_MAX);
}

/** Parse the embed-supplied framing-page URL (an absolute URL string, e.g. the maFrame
 * `ac[current_parent_url]`) into the host-page value `host + path`. Query/hash are dropped
 * by taking only `hostname`/`pathname`. Returns null when the string is absent or not an
 * absolute URL, so the caller falls back to best-effort detection rather than shipping junk.
 * Any nAPI-invalid characters that survive are dropped server-side (absence = no row). */
function hostPageFromUrl(url: string): string | null {
  try {
    const u = new URL(url);
    return normalizeHostPage(u.hostname, u.pathname);
  } catch {
    return null;
  }
}

/** The path charset nAPI's hostpage validation accepts (HOSTPAGE_PATH_REGEX in
 * apps/napi/src/server/trafficAttribution.ts). A parent path with any other character — a
 * percent-escape from a space or accent (`/airport%20parking`, `/caf%C3%A9`), a `@` or `:` —
 * is REJECTED server-side and writes NO row, which is strictly worse than the bare domain. So
 * a path that would not survive is dropped to host-only here, degrading to a real (if coarse)
 * row instead of vanishing. Keep in step with nAPI's regex. */
const NAPI_ACCEPTABLE_PATH = /^\/[A-Za-z0-9._~/-]*$/;

/** Assemble `host + path` for the host-page header: lowercase host, query/hash-free path,
 * trailing slash trimmed on non-root paths (so `/book` and `/book/` are one row), a path nAPI
 * would reject dropped to host-only, capped. Used for the EMBEDDED parent page (its full URL)
 * — never for the app's own page. */
function normalizeHostPage(hostname: string, pathname: string): string | null {
  const host = hostname.trim().toLowerCase();
  if (!host) return null;
  let path = pathname || "";
  if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
  // Degrade to host-only rather than let nAPI drop an unacceptable path to nothing.
  if (path && path !== "/" && !NAPI_ACCEPTABLE_PATH.test(path)) path = "";
  const value = path && path !== "/" ? `${host}${path}` : host;
  return value.slice(0, HOST_PAGE_MAX);
}
